import React, { useRef, useEffect } from 'react';

const NeuralAI = () => {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const isMobile = globalThis.matchMedia('(max-width: 640px)').matches;
    const frameInterval = isMobile ? 1000 / 30 : 1000 / 60;
    let size = 320;
    let center = { x: size / 2, y: size / 2 };
    let neurons = [];
    let fibers = [];
    let isVisible = true;
    let lastFrameTime = 0;

    let frame;

    const buildScene = () => {
      const rect = wrapper.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const minSize = isMobile ? 230 : 220;
      const maxSize = isMobile ? 270 : 520;
      const nextSize = Math.max(minSize, Math.min(maxSize, Math.floor(Math.min(rect.width, rect.height))));

      size = nextSize;
      center = { x: size / 2, y: size / 2 };

      canvas.width = Math.floor(size * dpr);
      canvas.height = Math.floor(size * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      neurons = [];
      fibers = [];

      let neuronCount = 80;
      if (isMobile) {
        neuronCount = 52;
      } else if (size < 320) {
        neuronCount = 56;
      }

      for (let i = 0; i < neuronCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = size * (isMobile ? 0.08 + Math.random() * 0.3 : 0.08 + Math.random() * 0.38);
        neurons.push({
          x: center.x + Math.cos(angle) * dist,
          y: center.y + Math.sin(angle) * dist,
          baseX: center.x + Math.cos(angle) * dist,
          baseY: center.y + Math.sin(angle) * dist,
          radius: 1 + Math.random() * 3,
          pulse: Math.random() * Math.PI * 2,
          speed: 0.5 + Math.random() * 2,
          brightness: 0.3 + Math.random() * 0.7,
        });
      }

      for (let i = 0; i < neurons.length; i++) {
        for (let j = i + 1; j < neurons.length; j++) {
          const dx = neurons[i].baseX - neurons[j].baseX;
          const dy = neurons[i].baseY - neurons[j].baseY;
          const dist = Math.hypot(dx, dy);
          if (dist < size * (isMobile ? 0.24 : 0.22) && Math.random() > (isMobile ? 0.5 : 0.4)) {
            fibers.push({ a: i, b: j, dist, pulseOffset: Math.random() * Math.PI * 2 });
          }
        }
      }
    };

    const animate = (time) => {
      if (!isVisible || document.hidden) {
        frame = requestAnimationFrame(animate);
        return;
      }

      if (time - lastFrameTime < frameInterval) {
        frame = requestAnimationFrame(animate);
        return;
      }

      lastFrameTime = time;
      const t = time / 1000;
      ctx.clearRect(0, 0, size, size);

      // Amorphous autonomous drift: multiple overlapping sine waves at different frequencies
      const driftX = Math.sin(t * 0.17) * 0.35 + Math.sin(t * 0.07 + 1.3) * 0.25 + Math.sin(t * 0.31 + 2.7) * 0.15;
      const driftY = Math.cos(t * 0.13 + 0.5) * 0.35 + Math.cos(t * 0.09 + 2.1) * 0.25 + Math.cos(t * 0.23 + 0.8) * 0.15;

      // Update neuron positions (autonomous amorphous motion)
      neurons.forEach((n) => {
        n.x = n.baseX + driftX * 22 + Math.sin(t * n.speed + n.pulse) * 3;
        n.y = n.baseY + driftY * 22 + Math.cos(t * n.speed + n.pulse) * 3;
      });

      // Draw fibers
      fibers.forEach((f) => {
        const a = neurons[f.a];
        const b = neurons[f.b];
        const pulse = (Math.sin(t * 2 + f.pulseOffset) + 1) / 2;
        const alpha = 0.05 + pulse * 0.15;

        ctx.beginPath();
        ctx.moveTo(a.x, a.y);

        // Curved fiber
        const midX = (a.x + b.x) / 2 + Math.sin(t + f.pulseOffset) * 8;
        const midY = (a.y + b.y) / 2 + Math.cos(t + f.pulseOffset) * 8;
        ctx.quadraticCurveTo(midX, midY, b.x, b.y);

        ctx.strokeStyle = `rgba(59, 130, 246, ${alpha})`;
        ctx.lineWidth = 0.5 + pulse * 0.8;
        ctx.stroke();

        // Traveling pulse dot
        const pulsePos = (t * 0.3 + f.pulseOffset) % 1;
        const px = a.x + (b.x - a.x) * pulsePos;
        const py = a.y + (b.y - a.y) * pulsePos;
        ctx.beginPath();
        ctx.arc(px, py, 1 + pulse, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(96, 165, 250, ${pulse * 0.6})`;
        ctx.fill();
      });

      // Draw neurons
      neurons.forEach((n) => {
        const pulse = (Math.sin(t * n.speed + n.pulse) + 1) / 2;

        // Glow
        const gradient = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.radius * 6);
        gradient.addColorStop(0, `rgba(59, 130, 246, ${0.15 * n.brightness * pulse})`);
        gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius * 6, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius * (0.8 + pulse * 0.4), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(147, 197, 253, ${0.5 + pulse * 0.5})`;
        ctx.fill();
      });

      // Central glow
      const centralGlowRadius = size * 0.2;
      const centralGlow = ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, centralGlowRadius);
      centralGlow.addColorStop(0, `rgba(59, 130, 246, ${0.08 + Math.sin(t * 0.8) * 0.04})`);
      centralGlow.addColorStop(0.5, `rgba(59, 130, 246, ${0.03 + Math.sin(t * 0.8) * 0.02})`);
      centralGlow.addColorStop(1, 'rgba(59, 130, 246, 0)');
      ctx.beginPath();
      ctx.arc(center.x, center.y, centralGlowRadius, 0, Math.PI * 2);
      ctx.fillStyle = centralGlow;
      ctx.fill();


      // Inner ring of small pulsing nodes
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2 + t * 0.3;
        const r = size * 0.115 + Math.sin(t * 1.5 + i) * (size * 0.01);
        const nx = center.x + Math.cos(a) * r;
        const ny = center.y + Math.sin(a) * r;
        const np = (Math.sin(t * 2 + i * 0.5) + 1) / 2;

        ctx.beginPath();
        ctx.arc(nx, ny, 1.5 + np, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(96, 165, 250, ${0.3 + np * 0.5})`;
        ctx.fill();
      }

      frame = requestAnimationFrame(animate);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = !!entry?.isIntersecting;
      },
      { threshold: 0.05 },
    );

    observer.observe(wrapper);

    buildScene();
    frame = requestAnimationFrame(animate);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="w-full h-full flex items-center justify-center"
      style={{ pointerEvents: 'none' }}
    >
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', maxWidth: 520, maxHeight: 520, pointerEvents: 'none' }}
      />
    </div>
  );
};

export default NeuralAI;
